/**
 * Shared form serialization used by ReferenceModal and QuickAddCollectModal.
 */

export function processReferenceFormData(form, modelFields, localVisualGroups) {
  const dataToSave = { ...form };
  const isEmpty = (value) => {
    if (value === null || value === undefined) return true;
    if (value === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (typeof value === "object" && Object.keys(value).length === 0)
      return true;
    return false;
  };

  const isFieldHidden = (field) => !!field.hide;

  const cleanObject = (obj, fields) => {
    const cleaned = {};
    fields.forEach((field) => {
      if (isFieldHidden(field)) return;

      const value = obj[field.key];
      if (field.isArray && (value == null || value.length === 0)) {
        cleaned[field.key] = null;
      } else {
        cleaned[field.key] = value;
      }
    });
    return cleaned;
  };

  const processFields = (data, fields) => {
    Object.keys(data).forEach((key) => {
      const fieldDef = fields.find((f) => f.key === key);

      if (fieldDef) {
        if (
          fieldDef.type === "reference" &&
          data[key] &&
          typeof data[key] === "object" &&
          data[key]._id
        ) {
          data[key] = data[key]._id;
        } else if (fieldDef.type === "date" && data[key]) {
          // keep as text
        } else if (
          fieldDef.type === "group" &&
          data[key] &&
          typeof data[key] === "object"
        ) {
          if (fieldDef.isArray && Array.isArray(data[key])) {
            data[key] = data[key]
              .map((item) => {
                if (item && typeof item === "object") {
                  const _id = item._id;
                  const cleanedItem = cleanObject(item, fieldDef.fields);
                  if (Object.keys(cleanedItem).length > 0) {
                    const processedItem = processFields(
                      cleanedItem,
                      fieldDef.fields
                    );
                    if (_id) {
                      processedItem._id = _id;
                    }
                    return processedItem;
                  }
                }
                return null;
              })
              .filter((item) => item !== null);
          } else {
            const _id = data[key]._id;
            data[key] = cleanObject(data[key], fieldDef.fields);
            if (Object.keys(data[key]).length > 0) {
              const processedGroup = processFields(
                data[key],
                fieldDef.fields
              );
              if (_id) {
                processedGroup._id = _id;
              }
              data[key] = processedGroup;
            }
          }
        } else if (Array.isArray(data[key])) {
          data[key] = data[key]
            .map((item) => {
              if (item && typeof item === "object") {
                if (fieldDef.type === "reference" && item._id) {
                  return item._id;
                }
              }
              return item;
            })
            .filter((item) => !isEmpty(item));
        } else {
          const value = data[fieldDef.key];
          if (fieldDef.type === "file") {
            data[fieldDef.key] = value;
          } else if (!isEmpty(value)) {
            data[fieldDef.key] = value;
          } else {
            data[fieldDef.key] = null;
          }
        }
      }
    });
    return data;
  };

  if (localVisualGroups && localVisualGroups.length > 0) {
    localVisualGroups.forEach((group) => {
      const groupKey = `group_${group.name}`;
      if (dataToSave[groupKey]) {
        dataToSave[groupKey] = processFields(
          dataToSave[groupKey],
          modelFields
        );
        Object.keys(dataToSave[groupKey]).forEach((fieldKey) => {
          dataToSave[fieldKey] = dataToSave[groupKey][fieldKey];
        });
        delete dataToSave[groupKey];
      }
    });
  }

  processFields(dataToSave, modelFields);

  return dataToSave;
}

/** Identity / audit keys that must not be reused when cloning group rows. */
const GROUP_ROW_IDENTITY_KEYS = [
  "_id",
  "idModel",
  "parent",
  "createdAt",
  "updatedAt",
  "createdBy",
  "updatedBy",
  "__v",
  "editHistory",
  "undefined",
];

function stripGroupRowIdentity(item) {
  if (!item || typeof item !== "object") return item;
  GROUP_ROW_IDENTITY_KEYS.forEach((k) => {
    delete item[k];
  });
  return item;
}

/**
 * Recursively strip group-row identity so duplicate/create always inserts new
 * Proform_productandprice (etc.) docs instead of reparenting the source rows.
 * Mutates `data` in place.
 */
export function stripGroupDocumentIds(data, fields, { force = false } = {}) {
  if (!data || !fields) return data;
  const shouldStripIdentity = force || !data._id;
  fields.forEach((field) => {
    const key = field.key;
    if (field.type !== "group" || !data[key]) return;
    if (field.isArray && Array.isArray(data[key])) {
      data[key] = data[key].map((item) => {
        if (!item || typeof item !== "object") return item;
        const newItem = { ...item };
        if (shouldStripIdentity) {
          stripGroupRowIdentity(newItem);
        } else {
          delete newItem.updatedAt;
          delete newItem.createdAt;
          delete newItem.editHistory;
          delete newItem.undefined;
          delete newItem.createdBy;
          delete newItem.updatedBy;
          delete newItem.__v;
        }
        stripGroupDocumentIds(newItem, field.fields, { force: shouldStripIdentity });
        return newItem;
      });
    } else if (typeof data[key] === "object") {
      if (shouldStripIdentity) {
        stripGroupRowIdentity(data[key]);
      } else {
        delete data[key].updatedAt;
        delete data[key].createdAt;
        delete data[key].editHistory;
        delete data[key].undefined;
        delete data[key].createdBy;
        delete data[key].updatedBy;
        delete data[key].__v;
      }
      stripGroupDocumentIds(data[key], field.fields, { force: shouldStripIdentity });
    }
  });
  return data;
}

export function removeReferenceGroupIds(data, fields) {
  stripGroupDocumentIds(data, fields, { force: false });
}

/** Ref de grupo solo con _id (duplicar PI copió ObjectIds sin datos embebidos). */
function isBareGroupIdRef(entry) {
  if (typeof entry === "string") {
    return /^[a-f0-9]{24}$/i.test(entry.trim());
  }
  if (!entry || typeof entry !== "object" || Array.isArray(entry.products)) {
    return false;
  }
  const keys = Object.keys(entry);
  if (!keys.length) return false;
  const contentKeys = keys.filter((k) => !["_id", "id", "parent"].includes(k));
  return contentKeys.length === 0;
}

/**
 * Expande refs de grupo (solo _id) a documentos completos antes de duplicar.
 */
export async function expandGroupIdRefsForDuplicate(
  itemCopy,
  fields,
  parentModelName,
  fetchGroupDoc
) {
  if (!itemCopy || !fields || !parentModelName || !fetchGroupDoc) return;
  for (const field of fields) {
    if (field.type !== "group" || !itemCopy[field.key]) continue;
    const val = itemCopy[field.key];
    if (!Array.isArray(val) || val.length === 0) continue;
    if (!val.every(isBareGroupIdRef)) continue;

    const groupModelName = `${parentModelName}_${field.key}`;
    const expanded = [];
    for (const ref of val) {
      const id =
        typeof ref === "string" ? ref.trim() : ref && ref._id ? String(ref._id) : "";
      if (!id) continue;
      try {
        const doc = await fetchGroupDoc(groupModelName, id);
        if (doc) expanded.push(doc);
      } catch (e) {
        console.warn("[expandGroupIdRefsForDuplicate]", groupModelName, id, e);
      }
    }
    if (expanded.length) {
      itemCopy[field.key] = expanded;
    }
  }
}
